import React from 'react'

// WRDS-771: store-submission privacy policy (Google Play User Data policy +
// Apple 5.1.1). Static page on purpose — both stores require a stable,
// public, non-editable URL, so this must not depend on CMS content existing.
function PrivacyPolicy() {
  return (
    <div className='privacy-policy-pages py-5'>
      <div className='container'>
        <div className='page-header'>
          <h1>WiL Reji Privacy Policy</h1>
        </div>
        <div className='cms-wraper'>
          <p><strong>Effective Date: July 12, 2026</strong></p>
          <p>
            This Privacy Policy applies to the <strong>WiL Reji</strong> point-of-sale service, including the{' '}
            <strong>WiL Reji Waiter</strong> and <strong>WiL Reji Cashier</strong> mobile applications and related web
            services (together, the &quot;Service&quot;), provided by <strong>WiLLDesign Inc.</strong>{' '}
            (&quot;WiLLDesign&quot;, &quot;we&quot;, &quot;us&quot;), a company registered in Tokyo, Japan.
            Representative: Sanjay Bhandari.
          </p>
          <p>
            We are committed to protecting the personal information of our users in accordance with Japan&apos;s Act on
            the Protection of Personal Information (APPI) and other applicable laws. By creating an account or using the
            Service, you agree to the practices described in this Policy, including the international handling of data
            described in Section 8.
          </p>

          <h2>1. Information We Collect</h2>
          <h3>1.1 Information you provide</h3>
          <ul className='list-li-dot'>
            <li>Account information: name, email address, phone number</li>
            <li>Store/business information: store name, address, menu, staff account details</li>
          </ul>
          <br></br>
          <h3>1.2 Information collected automatically</h3>
          <ul className='list-li-dot'>
            <li>Device and connection information: IP address, device model, OS version, app version</li>
            <li>Usage logs: features used, actions taken in the app, error and diagnostic logs</li>
          </ul>
          <br></br>
          <h3>1.3 Information processed on behalf of your store</h3>
          <ul className='list-li-dot'>
            <li>Store customer information entered by you or your staff, including order history and order contents</li>
            <li>Sales and transaction data generated through your use of the Service</li>
          </ul>
          <p>
            Where your store&apos;s customers&apos; information is entered into the Service, your store remains
            responsible for that data as the business handling it; we process it to provide the Service to you.
          </p>
          <br></br>

          <h2>2. Bluetooth and Location Permissions</h2>
          <p>
            The apps request Bluetooth permissions solely to discover and connect to Epson receipt printers near your
            device.
          </p>
          <p>
            On devices running Android 11 or earlier, the Android operating system requires location permission in order
            to scan for nearby Bluetooth devices. We request this permission <strong>only</strong> to enable printer
            discovery. <strong>We do not collect, store, or transmit your location.</strong> On iOS, Bluetooth access is
            used only to connect to receipt printers.
          </p>
          <br></br>

          <h2>3. How We Use Information</h2>
          <p>We use the information described above to:</p>
          <ul className='list-li-dot'>
            <li>Verify your identity and manage your account</li>
            <li>Provide, operate, and maintain the Service, including order processing and receipt printing</li>
            <li>Process payments and billing</li>
            <li>
              Analyze usage to improve the Service and develop new features, including improvement of AI-assisted
              features using only statistical or aggregated information that cannot be used to identify any individual
            </li>
            <li>Respond to inquiries and troubleshoot problems</li>
            <li>
              Send service announcements and, with your consent, marketing communications about our own products (you
              can opt out at any time via the unsubscribe link or by contacting us)
            </li>
            <li>Protect the security of the Service and prevent fraud or abuse</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>
            <strong>
              We do not use your data for third-party advertising, and our apps do not track you across other
              companies&apos; apps or websites.
            </strong>{' '}
            Our apps contain no advertising SDKs.
          </p>
          <br></br>

          <h2>4. Disclosure to Third Parties</h2>
          <p>
            We do not sell personal information, and we do not share personal information with third parties for their
            own marketing or advertising purposes.
          </p>
          <p>We disclose personal information only in the following cases:</p>
          <ul className='list-li-dot'>
            <li>
              <strong>Service providers (entrusted processing):</strong> we engage contractors for system development,
              cloud infrastructure operation, and sales support. These providers act on our instructions under written
              contracts that include confidentiality obligations and data-protection requirements providing the same or
              equal level of protection of user data as described in this Policy. Our cloud infrastructure is hosted
              with Amazon Web Services in the Tokyo, Japan region.
            </li>
            <li>
              <strong>Legal requirements:</strong> where disclosure is required by law, court order, or lawful request
              of a public authority.
            </li>
            <li>
              <strong>Business succession:</strong> in connection with a merger or business transfer, subject to this
              Policy.
            </li>
          </ul>
          <br></br>

          <h2>5. Cookies and Similar Technologies</h2>
          <p>The WiL Reji Waiter and WiL Reji Cashier mobile apps do not use cookies.</p>
          <br></br>

          <h2>6. Security</h2>
          <p>
            We implement organizational and technical safeguards including role-based access controls, staff access
            limitation, and encryption of data in transit. No method of transmission or storage is completely secure,
            but we work continuously to protect your information.
          </p>
          <br></br>

          <h2>7. Data Retention and Deletion</h2>
          <p>We retain personal information only as long as necessary for the purposes above:</p>
          <ul className='list-li-dot'>
            <li>
              <strong>Account information:</strong> for the life of your account, deleted within 30 days after account
              deletion
            </li>
            <li>
              <strong>Sales and transaction records:</strong> up to 7 years after creation, to meet Japanese bookkeeping
              and tax requirements
            </li>
            <li>
              <strong>Usage and diagnostic logs:</strong> up to 12 months
            </li>
            <li>Data we are legally required to keep is retained for the legally mandated period and then deleted.</li>
          </ul>
          <p>When retention ends, data is deleted or irreversibly anonymized.</p>
          <br></br>

          <h2>8. International Data Handling</h2>
          <p>
            Personal information is stored in Japan. Some of our contracted service providers performing system
            development and support are located in <strong>India</strong> and <strong>Nepal</strong> and may access
            personal information in the course of providing those services.
          </p>
          <p>
            Neither India nor Nepal has been designated by Japan&apos;s Personal Information Protection Commission as a
            country with a data-protection regime equivalent to Japan&apos;s. We therefore require these providers, by
            contract, to continuously implement measures for the handling of personal information equivalent to the
            standards required of businesses under the APPI, and we periodically verify their implementation.
            Information about the data-protection systems of these countries and the measures taken by these providers
            is available on request via the contact in Section 12. By creating an account, you consent to this handling.
          </p>
          <br></br>

          <h2>9. Your Rights</h2>
          <p>
            You may request access to, correction of, cessation of use of, or deletion of your personal information at
            any time:
          </p>
          <ul className='list-li-dot'>
            <li>
              <strong>Account deletion in the web dashboard:</strong> restaurant owners can open the{' '}
              <strong>Profile</strong> page in the WiL Reji web dashboard and select <strong>Delete Account</strong>.
              This deletes your account and associated personal data, except data we must retain by law (Section 7).
            </li>
            <li>
              <strong>Web deletion request:</strong> you can also request account and data deletion without the app at{' '}
              <a href='/delete-account'>wilreji.com/delete-account</a>.
            </li>
            <li>
              <strong>Other requests (APPI disclosure/correction):</strong> email{' '}
              <a href='mailto:contact@willdesign-tech.com'>contact@willdesign-tech.com</a> with your account email. We
              will verify your identity and respond in writing or electronically without undue delay. There is no fee
              for these requests.
            </li>
          </ul>
          <p>
            You may withdraw consent to marketing communications at any time without affecting your use of the Service.
          </p>
          <br></br>

          <h2>10. Age Requirement</h2>
          <p>
            The Service is intended for business use by persons <strong>18 years of age or older</strong>. We do not
            knowingly collect personal information from anyone under 18. If we learn that we have done so, we will
            delete it promptly.
          </p>
          <br></br>

          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Policy from time to time. Material changes will be announced on our website and/or by
            email before they take effect, and the Effective Date above will be updated. Continued use of the Service
            after changes take effect constitutes acceptance.
          </p>
          <br></br>

          <h2>12. Contact</h2>
          <p>
            WiLLDesign Inc. (Tokyo, Japan)
            <br></br>
            Representative: Sanjay Bhandari
            <br></br>
            Email: <a href='mailto:contact@willdesign-tech.com'>contact@willdesign-tech.com</a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
